import { Component } from '@theme/component';

/**
 * Embla-powered carousel for the product media gallery.
 * Sits inside <media-gallery ref="slideshow"> so media-gallery.js needs no changes.
 * Exposes previous/next/select to match the slideshow-component interface.
 * Exposes dragging getter so media-gallery.js can block zoom on drag.
 * Syncs the slideshow-controls (thumbnails, dots, and counter) on every Embla
 * select, since Embla replaces the native scroll that those controls relied on.
 */
class MediaGalleryCarousel extends Component {
  #embla = null;
  #dragging = false;
  #viewport = null;

  connectedCallback() {
    super.connectedCallback();

    const viewport = this.querySelector('.mgc__viewport');
    if (!viewport || !window.EmblaCarousel) return;
    this.#viewport = viewport;

    this.#embla = window.EmblaCarousel(viewport, {
      loop: true,
      align: 'center',
      containScroll: false,
    });

    // The viewport is a single focusable carousel region: Tab lands here first, Left/Right
    // arrows move between slides, then Tab continues into the active slide's controls (e.g. a
    // video's play button, since the active slide is not inert) and on to the thumbnails.
    viewport.setAttribute('tabindex', '0');
    viewport.setAttribute('role', 'group');
    viewport.setAttribute('aria-roledescription', 'carousel');
    viewport.setAttribute('aria-label', this.dataset.label || 'Product media');
    viewport.addEventListener('keydown', this.#onKeydown);

    this.#embla.on('select', () => {
      this.#syncControls();
      this.#syncSlides();
      this.#announce();
    });
    this.#syncControls();
    this.#syncSlides();
    this.#trackDragging(viewport);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#viewport?.removeEventListener('keydown', this.#onKeydown);
    this.#embla?.destroy();
    this.#embla = null;
  }

  // Left/Right arrow navigation while the slideshow region (or its content) has focus.
  #onKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      this.#embla?.scrollPrev();
      event.preventDefault();
    } else if (event.key === 'ArrowRight') {
      this.#embla?.scrollNext();
      event.preventDefault();
    }
  };

  get dragging() {
    return this.#dragging;
  }

  // Called by on:click="/zoom/N" on a slide. Horizon resolves "/method" to the nearest ancestor
  // Component, which is this carousel (not <media-gallery>). Block the zoom mid-drag, otherwise
  // delegate to the gallery which owns the zoom dialog.
  zoom(index, event) {
    if (this.#dragging) return;
    const gallery = /** @type {any} */ (this.closest('media-gallery'));
    if (gallery && typeof gallery.zoom === 'function') gallery.zoom(index, event);
  }

  // Called by on:click="/previous" on the arrow button
  previous() {
    this.#embla?.scrollPrev();
  }

  // Called by on:click="/next" on the arrow button
  next() {
    this.#embla?.scrollNext();
  }

  // Called by on:click="/select/N" on thumbnails + by media-gallery.js for zoom sync
  select(input, _event, options = {}) {
    if (!this.#embla) return;
    const index = typeof input === 'number' ? input : parseInt(input, 10);
    if (isNaN(index)) return;
    this.#embla.scrollTo(index, options.animate === false);
  }

  // Track drag via pointer events. Embla preventDefaults pointerdown, which suppresses the
  // compat mouse events — so listening to pointer events (not mouse/touch) is what fires reliably.
  // Movement is only tracked while the pointer is down, otherwise plain hover movement (which also
  // fires pointermove) would falsely flag a drag and block the zoom click.
  // Threshold of 5px sits below Embla's own dragThreshold (10px), so any real Embla drag is caught
  // while tiny jitters still allow a click to open zoom.
  #trackDragging(viewport) {
    let startX = 0;
    let pointerDown = false;

    viewport.addEventListener('pointerdown', (e) => {
      pointerDown = true;
      startX = e.clientX;
      this.#dragging = false;
    });

    viewport.addEventListener('pointermove', (e) => {
      if (pointerDown && Math.abs(e.clientX - startX) > 5) this.#dragging = true;
    });

    viewport.addEventListener('pointerup', () => {
      pointerDown = false;
    });

    viewport.addEventListener('pointercancel', () => {
      pointerDown = false;
    });
  }

  // Only the selected slide should be interactive / exposed to assistive tech. Embla keeps every
  // slide in the DOM, so without this the off-screen slides' focusable content (native video
  // controls, zoom buttons, model-viewer) stays in the tab order — tabbing jumps to an off-screen
  // slide and the browser's scroll-into-view yanks the carousel to it. `inert` removes the whole
  // subtree from both focus order and the accessibility tree; aria-hidden is kept in sync too.
  #syncSlides() {
    const selected = this.#embla?.selectedScrollSnap() ?? 0;
    this.querySelectorAll('.mgc__container > slideshow-slide').forEach((slide, i) => {
      const isActive = i === selected;
      slide.toggleAttribute('inert', !isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    });
  }

  // Announces the slide change to screen readers, since changing slides via a button keeps focus
  // on the button — VoiceOver otherwise gives no feedback that the displayed media changed.
  #announce() {
    const status = this.querySelector('[data-mgc-status]');
    if (!status || !this.#embla) return;
    const current = this.#embla.selectedScrollSnap() + 1;
    const total = this.#embla.slideNodes().length;
    status.textContent = `Slide ${current} of ${total}`;
  }

  #syncControls() {
    const selected = this.#embla?.selectedScrollSnap() ?? 0;

    // Each slideshow-controls renders exactly one control type (thumbnails, dots, or counter),
    // and carousel mode can render separate desktop + mobile control sets — so sync each
    // slideshow-controls independently to keep per-group indices correct.
    this.querySelectorAll('slideshow-controls').forEach((controls) => {
      controls.querySelectorAll('[ref="thumbnails[]"], [ref="dots[]"]').forEach((item, i) => {
        item.setAttribute('aria-selected', String(i === selected));
      });

      const counter = controls.querySelector('[ref="current"]');
      if (counter) counter.textContent = String(selected + 1);
    });
  }
}

if (!customElements.get('media-gallery-carousel')) {
  customElements.define('media-gallery-carousel', MediaGalleryCarousel);
}
